// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Button,
  ClickAwayListener,
  Collapse,
  InputBase,
  Stack,
  TypographyVariant,
  styled,
  useTheme,
} from '@mui/material';
import classNames from 'classnames';
import {
  ChangeEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import { NumericFormat } from 'react-number-format';

// Saved numbers are padded to at least this many decimals, never rounded.
const MINIMUM_NUMBER_DECIMALS = 2;

const Container = styled(Stack)(({ theme }) => ({
  marginLeft: '-8px',
  transition: theme.transitions.create([
    'background-color',
    'box-shadow',
    'border-color',
  ]),
  maxWidth: 'calc(100% + 8px)',
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
  paddingTop: '4px',
  paddingLeft: '8px',
  paddingBottom: '4px',
  border: '1px solid transparent',
  '&:hover': {
    backgroundColor: 'hsl(0, 0%, 95%)',
  },
  '&.active': {
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    backgroundColor: 'white',
    boxShadow: theme.shadows[8],
    cursor: 'text',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  padding: '4px 8px',
  minWidth: 0,
  fontSize: theme.typography.caption.fontSize,
}));

const Input = styled(InputBase)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  alignItems: 'center',
  marginRight: 'auto',
  minWidth: '100px',
  cursor: 'inherit',
  textarea: {
    cursor: 'inherit',
    position: 'absolute',
    width: '100%',
    left: 0,
    maxWidth: '100%',
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
  },
  '&::after': {
    maxWidth: '100%',
    overflowWrap: 'break-word',
    display: 'block',
    content: "attr(data-value) ' '",
    visibility: 'hidden',
  },
}));

interface Props {
  /**
   * The current value of the editable text.
   */
  value: string | null;

  /**
   * A placeholder value to display when the current value is null.
   */
  placeholder: string;

  /**
   * The variant of the typography component. This defaults to 'body2'.
   */
  variant?: TypographyVariant;

  /**
   * The type of the data being entered. This defaults to 'string'.
   */
  type?: 'number' | 'string';

  /**
   * If true, the component will be disabled.
   */
  disabled?: boolean;

  /**
   * A callback fired when the value is saved.
   */
  onChange: (value: string) => void;
}

/**
 * A component that renders a text label that transform into an input when
 * clicked.
 */
export function EditableTypography(props: Props) {
  const theme = useTheme();
  const textField = useDisclosure();
  const inputElement = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState(props.value ?? '');

  // Sync internal state with props when props.value changes externally
  // Only sync if the text field is not currently being edited
  useEffect(() => {
    if (!textField.isOpen) {
      setValue(props.value ?? '');
    }
  }, [props.value, textField.isOpen]);

  const variant = props.variant ?? 'body2';
  const type = props.type ?? 'string';
  const dataValue = value.trim() === '' ? props.placeholder : value;
  const className = classNames({ active: textField.isOpen });

  const handleClick = () => {
    textField.open();

    inputElement.current?.focus();
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setValue(event.target.value);
  };

  const handleClickAway = () => {
    if (value === '' && props.value === null) {
      textField.close();
    }

    if (value === props.value) {
      textField.close();
    }
  };

  const handleClickSave: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    const trimmedValue = value.trim();

    if (type === 'number') {
      const parsedValue = parseCurrencyAmount(trimmedValue.replace(/,/g, ''));
      const nullSafeValue =
        parsedValue === null
          ? ''
          : parsedValue.toFixed(
              Math.max(MINIMUM_NUMBER_DECIMALS, parsedValue.decimalPlaces())
            );
      props.onChange(nullSafeValue);
      setValue(nullSafeValue);
    } else {
      props.onChange(trimmedValue);
      setValue(trimmedValue);
    }
    textField.close();
  };

  const handleClickCancel: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    setValue(props.value ?? '');
    textField.close();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    // For number fields, prevent Enter key to avoid form submission
    // For string fields (multiline), allow Enter to create new lines
    if (event.key === 'Enter' && type === 'number') {
      event.preventDefault();
    }
  };

  const handleKeyUp: KeyboardEventHandler<HTMLInputElement> = (event) => {
    // For number fields, prevent Enter key to avoid form submission
    // For string fields (multiline), allow Enter to create new lines
    if (event.key === 'Enter' && type === 'number') {
      event.preventDefault();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Container
        useFlexGap
        spacing={1}
        direction="row"
        className={className}
        onClick={handleClick}
      >
        {type === 'number' ? (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            disabled={props.disabled}
            data-value={dataValue}
            placeholder={props.placeholder}
            value={value}
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
          />
        ) : (
          <Input
            multiline
            spellCheck={textField.isOpen}
            disabled={props.disabled}
            inputRef={inputElement}
            data-value={dataValue}
            placeholder={props.placeholder}
            value={value}
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            sx={theme.typography[variant]}
          />
        )}
        <Collapse in={textField.isOpen} orientation="horizontal">
          <Stack direction="row" paddingRight={1}>
            <ActionButton onClick={handleClickCancel}>Cancel</ActionButton>
            <ActionButton onClick={handleClickSave}>Save</ActionButton>
          </Stack>
        </Collapse>
      </Container>
    </ClickAwayListener>
  );
}
